import {
    CountedProductFilters,
    GetProductsQuery,
    ProductFilters,
    ProductList,
    ProductSortInput,
} from 'generated/graphql';
import { useServerTranslation } from 'hooks/useServerTranslation';
import { useSnackbar } from 'notistack';
import { PropsWithChildren, createContext, useContext, useEffect, useRef } from 'react';
import { StateCreator, create, useStore } from 'zustand';

type ProductsType = NonNullable<GetProductsQuery['products']>;

type ProductsStoreTyp = {
    products: ProductsType['items'];
    slug: string;
    sorting?: ProductSortInput;
    size: number;
    from: number;
    total: number;
    showing: number;
    productFilters: Omit<CountedProductFilters, '__typename'>;
    selectedFilters: Omit<ProductFilters, '__typename'>;
    selectFilter: (filterKey: keyof Omit<CountedProductFilters, '__typename'>, filterName: string) => void;
    updateState: (products: ProductsType, slug?: string) => void;
    nextPage: () => boolean;
    reset: () => void;
    resetFilters: () => void;
    sortBy: (sorting: ProductSortInput) => void;
};

const initialState = {
    products: [],
    size: 0,
    total: 0,
    showing: 0,
    from: 0,
    sorting: undefined,
    slug: '',
    productFilters: {
        colorName: [],
        skinTone: [],
        productSeries: [],
        skinType: [],
        usageRegion: [],
        makeupProperties: [],
        mkProductType: [],
        fragrance: [],
        finish: [],
        seminarYear: [],
    },
    selectedFilters: {
        colorName: [],
        skinTone: [],
        productSeries: [],
        skinType: [],
        usageRegion: [],
        makeupProperties: [],
        mkProductType: [],
        fragrance: [],
        finish: [],
        seminarYear: [],
    },
};

const productsStoreDefinition: StateCreator<ProductsStoreTyp> = (set, get) => ({
    ...initialState,
    reset: () => set(() => initialState),
    resetFilters: () => set((state) => ({
        selectedFilters: initialState.selectedFilters,
        sorting: undefined,
        slug: state.slug,
    })),
    sortBy: (sorting) => set(() => ({
        sorting: {
            ...sorting,
            viewValue: undefined,
        },
    })),
    nextPage: () => {
        const state = get();
        if (state.from + state.size >= state.total) {
            return false;
        }
        set(() => ({ from: state.from + state.size }));

        return true;
    },
    updateState: (products, slug) => set((state) => {
        const newSlug = slug !== state.slug;
        const productsData = {
            size: products.size,
            from: products.from,
            total: products.total,
            showing: products.from + products.items.length,
        };

        if (newSlug) {
            return {
                ...productsData,
                products: products.items,
                productFilters: products.productFilters,
                selectedFilters: initialState.selectedFilters,
                from: 0,
                slug,
            };
        }

        return {
            ...productsData,
            products: state.from > 0 ? removeDuplicateProducts([...state.products, ...products.items]) : products.items,
            productFilters: state.productFilters,
            slug,
        };
    }),

    selectFilter: (filterKey, filterName) => set((state) => ({
        ...state,
        selectedFilters: {
            ...state.selectedFilters,
            [filterKey]: toggleArrayString(state.selectedFilters[filterKey], filterName),
        },
        from: 0,
    })),
});

const createProductsStore = () => create<ProductsStoreTyp>(productsStoreDefinition);

type ProductsStore = ReturnType<typeof createProductsStore>;

function removeDuplicateProducts(products: ProductsType['items']) {
    return products.filter((product, index) => products.indexOf(product) === index);
}

function toggleArrayString(array: string[], value: string) {
    if (array.includes(value)) {
        return array.filter((v) => v !== value);
    }

    return [...array, value];
}

export function useUpdateProducts(data?: ProductList, slug?: string) {
    const updateState = useProductsContext((store) => store.updateState);

    useEffect(() => {
        if (data) {
            updateState(data, slug);
        }
    }, [data, slug, updateState]);

    return null;
}

export function useLoadMore() {
    const { t } = useServerTranslation();
    const { enqueueSnackbar } = useSnackbar();
    const nextPage = useProductsContext((store) => store.nextPage);
    const loadMore = () => {
        const hasMore = nextPage();

        if (!hasMore) {
            enqueueSnackbar(t('fetch-messages.no-more'), { variant: 'info' });
        }
    };

    return loadMore;
}

// Context for contextual zustand store of ProductsStore type
const ProductsContext = createContext<ProductsStore | null>(null);

export function ProductsProvider({ children }: PropsWithChildren) {
    const storeRef = useRef<ProductsStore>();

    if (!storeRef.current) {
        storeRef.current = createProductsStore();
    }

    return (
        <ProductsContext.Provider value={storeRef.current}>
            {children}
        </ProductsContext.Provider>
    );
}

export function useProductsContext<T>(
    selector: (state: ProductsStoreTyp) => T,
    equalityFn?: (left: T, right: T) => boolean,
): T {
    const store = useContext(ProductsContext);
    if (!store) throw new Error('Missing BearContext.Provider in the tree');

    return useStore(store, selector, equalityFn);
}
